import { Capacitor } from '@capacitor/core'
import { Keyboard } from '@capacitor/keyboard'
import type { FC } from 'react'
import React, { useEffect } from 'react'
import { colors } from 'settings/colors'
import type { TSelectedTab } from 'stores/detailsPanelStore/detailsPanelStore'
import { closeDetailsPanel, detailsPanelStore } from 'stores/detailsPanelStore/detailsPanelStore'
import { smallStore } from 'stores/smallStore/smallStore'
import { getWindowOrientation } from 'utils/getWindowOrientation'
import { useScreenSize } from '../../utils/useScreenSize'
import { BottomPanelHandle } from './Bottom/BottomPanelHandle'
import { getBottomPanelLayout } from './Bottom/getBottomPanelLayout'
import { getDraggedSize } from './getDraggedSize'
import { PanelTabs } from './PanelTabs/PanelTabs'
import { getRightPanelLayout } from './Right/getRightPanelLayout'
import { RightPanelHandle } from './Right/RightPanelHandle'
import { usePanelDrag } from './usePanelDrag'

export type TabPane = {
  key: TSelectedTab
  label: string
  icon: React.ReactNode
}

type DetailPanelProps = {
  children: ({
    width,
    height,
    isPanelExpanded,
  }: {
    width: number
    height: number
    isPanelExpanded: boolean
  }) => React.ReactNode
  tabs?: TabPane[]
  wrapperId?: string
}

export const DetailPanel: FC<DetailPanelProps> = ({ children, wrapperId, tabs }) => {
  const { isWideScreen, screenWidth, screenHeight } = useScreenSize()
  const mobileScreenOrientation = smallStore.useSelector((s) => s.mobileScreenOrientation)
  const screenOrientation = mobileScreenOrientation ?? getWindowOrientation(screenWidth, screenHeight)
  const placement = screenOrientation === 'landscape' ? 'right' : 'bottom'
  const { insets } = smallStore.useSelector((s) => s.safeArea)
  const isPanelOpen = detailsPanelStore.useSelector((s) => s.isPanelOpen)
  const draggedPanelSize = detailsPanelStore.useSelector((s) => s.draggedPanelSize)
  const panelDrag = usePanelDrag({ placement, isEnabled: isWideScreen })

  const layoutArgs = {
    viewportSize: { height: screenHeight, width: screenWidth },
    insets,
    isWideScreen,
    isOpen: isPanelOpen || panelDrag.isDragging,
    draggedSize: getDraggedSize({
      sizeDuringDrag: panelDrag.sizeDuringDrag,
      draggedPanelSize,
      placement,
      isWideScreen,
    }),
  }

  const layout = placement === 'right' ? getRightPanelLayout(layoutArgs) : getBottomPanelLayout(layoutArgs)
  const transition = panelDrag.isDragging ? undefined : 'top .3s, height .3s, left .3s, width .3s'

  const dragHandlers = panelDrag.getDragHandlers({
    isOpen: isPanelOpen,
    size: layout.size,
    maxSize: layout.maxSize,
  })

  //TODO, do we still need this?
  useEffect(() => {
    if (Capacitor.isNativePlatform()) {
      // Fix an issue with Mantine's useViewportSize hook not updating the viewport size after the keyboard is hidden on iOS
      const keyboardDidHide = Keyboard.addListener('keyboardDidHide', () => {
        window.dispatchEvent(new Event('resize'))
      })

      return () => {
        keyboardDidHide.then((listener) => listener.remove())
      }
    }
  }, [])

  // The map and Service Center panels share the open state, so each one starts closed
  useEffect(() => closeDetailsPanel, [])

  return (
    <div css={{ position: 'fixed', zIndex: 1001 }} id={wrapperId}>
      <div
        css={{
          position: 'fixed',
          overflow: 'auto',
          touchAction: 'pan-x pan-y',
          backgroundColor: colors.white,
        }}
        style={{ ...layout.content, transition }}
      >
        <div css={{ position: 'relative', display: 'flex', flexDirection: 'column', height: '100%' }}>
          {children({ width: layout.childWidth, height: layout.childHeight, isPanelExpanded: isPanelOpen })}
        </div>
      </div>
      {layout.placement === 'right' ? (
        <RightPanelHandle
          rect={layout.handle}
          transition={transition}
          isWideScreen={isWideScreen}
          dragHandlers={dragHandlers}
        />
      ) : (
        <BottomPanelHandle
          rect={layout.handle}
          transition={transition}
          isWideScreen={isWideScreen}
          dragHandlers={dragHandlers}
        />
      )}
      {!!tabs?.length && (
        <PanelTabs
          tabs={tabs}
          placement={placement}
          isWideScreen={isWideScreen}
          isOpen={isPanelOpen}
          position={layout.tabs}
          transition={transition}
        />
      )}
    </div>
  )
}
